import { NextResponse } from "next/server";
import { getSession } from "@/lib/auth/session";
import {
  getPlan,
  updatePlan,
  type UpdatablePlanFields,
} from "@/lib/db/queries/plans";
import { logActivity } from "@/lib/db/audit";

// Route handler de mutation des plans. Première mutation autorisée par le
// panel sur la DB en direct (cf. AGENTS.md "Périmètre des mutations admin").
//
// Mitigations :
// - Re-check is_superadmin avant d'agir (défense en profondeur, en plus
//   du proxy.ts).
// - Whitelist des champs au niveau Route Handler ET au niveau query
//   (defense en profondeur). Un caller ne peut pas glisser `password_hash`
//   ou un autre champ non prévu.
// - Audit avant l'UPDATE : on lit l'état précédent et on log un diff
//   structuré dans activities_log (table également auditable depuis le
//   journal). Si l'audit échoue, l'UPDATE est annulé (le throw remonte).

const VALIDATORS: Record<
  keyof UpdatablePlanFields,
  (v: unknown) => unknown | undefined
> = {
  name: (v) =>
    typeof v === "string" && v.trim().length > 0 && v.length <= 100
      ? v.trim()
      : undefined,
  // description : peut être null (champ effacé) ou string non vide ≤ 2 KB
  description: (v) => {
    if (v === null) return null;
    if (typeof v === "string") {
      const trimmed = v.trim();
      if (trimmed.length === 0) return null;
      if (trimmed.length > 2000) return undefined;
      return trimmed;
    }
    return undefined;
  },
  monthly_price: (v) => coerceMoney(v),
  yearly_price: (v) => coerceMoney(v),
  max_technicians: (v) => coerceCount(v),
  max_sites: (v) => coerceCount(v),
  max_machines: (v) => coerceCount(v),
  max_templates: (v) => coerceCount(v),
  features: (v) =>
    Array.isArray(v)
      ? v
          .map((x) => (typeof x === "string" ? x.trim() : ""))
          .filter((x) => x.length > 0 && x.length <= 100)
          .slice(0, 30)
      : undefined,
  visible: (v) => (typeof v === "boolean" ? v : undefined),
};

function coerceMoney(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n) || n < 0 || n > 999_999) return undefined;
  return Math.round(n * 100) / 100;
}

function coerceCount(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : Number(v);
  // Convention métier : -1 = illimité (pas de quota). 0 = limite à zéro
  // (techniquement valide, signifie que la fonctionnalité est désactivée).
  // Plage : -1 puis 0…100000.
  if (!Number.isInteger(n) || n < -1 || n > 100_000) return undefined;
  return n;
}

export async function PATCH(
  req: Request,
  { params }: { params: Promise<{ id: string }> },
) {
  const session = await getSession();
  if (!session?.user.is_superadmin) {
    return NextResponse.json(
      { success: false, error: "FORBIDDEN" },
      { status: 403 },
    );
  }

  const { id: idStr } = await params;
  const id = Number.parseInt(idStr, 10);
  if (!Number.isFinite(id) || id <= 0) {
    return NextResponse.json(
      { success: false, error: "INVALID_ID" },
      { status: 400 },
    );
  }

  const raw = (await req.json().catch(() => null)) as
    | Record<string, unknown>
    | null;
  if (!raw || typeof raw !== "object") {
    return NextResponse.json(
      { success: false, error: "INVALID_BODY" },
      { status: 400 },
    );
  }

  const validated: UpdatablePlanFields = {};
  const rejected: string[] = [];
  for (const key of Object.keys(VALIDATORS) as (keyof UpdatablePlanFields)[]) {
    if (!(key in raw)) continue;
    const cleaned = VALIDATORS[key](raw[key]);
    if (cleaned === undefined) {
      rejected.push(key);
      continue;
    }
    // Cast nécessaire — TS ne suit pas la corrélation key/type dans
    // la map de validators. La validation au-dessus garantit la cohérence.
    (validated as Record<string, unknown>)[key] = cleaned;
  }

  if (rejected.length > 0) {
    return NextResponse.json(
      {
        success: false,
        error: "VALIDATION_FAILED",
        rejected,
      },
      { status: 400 },
    );
  }

  if (Object.keys(validated).length === 0) {
    return NextResponse.json(
      { success: false, error: "NO_FIELDS" },
      { status: 400 },
    );
  }

  // Lecture du before pour audit, puis update, puis lecture du after.
  // Si le plan n'existe pas, 404 sans muter.
  const before = await getPlan(id);
  if (!before) {
    return NextResponse.json(
      { success: false, error: "NOT_FOUND" },
      { status: 404 },
    );
  }

  try {
    // L'audit est inséré AVANT l'UPDATE — si l'audit plante, on n'a pas
    // muté. Si l'UPDATE plante après, on a un audit "tentative" qui pointe
    // vers l'absence d'effet (la session admin verra l'incohérence en
    // rechargeant la liste des plans).
    await logActivity({
      type: "superadmin.plan_update",
      title: `Modification du plan ${before.code}`,
      description: JSON.stringify({
        plan_id: id,
        plan_code: before.code,
        changes: validated,
      }),
      user_id: session.user.id,
    });

    await updatePlan(id, validated);
    const after = await getPlan(id);
    return NextResponse.json({ success: true, data: after });
  } catch (err) {
    return NextResponse.json(
      {
        success: false,
        error: "DB_ERROR",
        message: err instanceof Error ? err.message : "unknown",
      },
      { status: 500 },
    );
  }
}
